import {
    AnalyticsUserEvent,
    type FreeToSpendPeriod,
    type FreeToSpendTransaction,
    type FreeToSpendWidgetProps,
} from '@fruitful/types';
import React, { useMemo, useState } from 'react';
import { BodyMediumRegular, LabelRegular, YStack } from 'smoothie';

import {
    type Transaction,
    TRANSACTION_TYPES_DESCRIPTION_MAP,
    type TransactionType,
} from '../../../banking/types/Transactions';
import { StorageKeys } from '../../../common/constants';
import { useStorage } from '../../../common/providers/StorageProvider';
import {
    FREE_TO_SPEND_EMBEDDED_DAY_LIST_MAX_HEIGHT,
    FreeToSpendTransactionsList,
} from '../../../home/components/free-to-spend/FreeToSpendTransactionsList';
import { SpendingStatusSheet } from '../../../home/components/free-to-spend/SpendingStatusSheet';
import { SpendingSummaryView } from '../../../home/components/free-to-spend/SpendingSummaryView';
import TransactionsSheet from '../../../home/components/free-to-spend/TransactionsSheet';
import {
    type FreeToSpendDataItem,
    type FreeToSpendDataPoint,
} from '../../../home/types/FreeToSpendTypes';
import { track } from '../../../logger/Logger';
import { useEnrichedTransaction } from '../../hooks/useEnrichedTransaction';
import { SproutTransactionDetailsSheet } from './SproutTransactionDetailsSheet';

const capitalize = (value: string): string =>
    value.length === 0 ? value : value.charAt(0).toUpperCase() + value.slice(1);

/** Map an agent-supplied transaction onto the app's richer Transaction type. */
const toTransaction = (transaction: FreeToSpendTransaction): Transaction => ({
    uuid: transaction.id,
    type: transaction.type,
    subtype: transaction.subtype ?? '',
    status: transaction.status,
    transactionTime: transaction.transactionTime,
    dcSign: transaction.dcSign,
    amount: transaction.amountCents,
    isPayment: transaction.isPayment ?? false,
    description: transaction.description,
    formattedType:
        TRANSACTION_TYPES_DESCRIPTION_MAP[
            transaction.type.toUpperCase() as TransactionType
        ] ?? '',
});

const readStoredSpendPeriod = (
    stored: string | null | undefined,
): FreeToSpendPeriod | undefined =>
    stored === 'weekly' || stored === 'monthly' ? stored : undefined;

/**
 * Explicit agent period wins; otherwise honor the home Spend period preference
 * (same key as SpendAccountSettingsSheet). Default monthly.
 */
const resolveSpendPeriod = (
    explicitPeriod: FreeToSpendPeriod | undefined,
    storedPeriod: string | null | undefined,
): FreeToSpendPeriod =>
    explicitPeriod ?? readStoredSpendPeriod(storedPeriod) ?? 'monthly';

/**
 * Sprout chat widget for free-to-spend. A thin, agent-prop-driven adapter over
 * the shared presentational surfaces used by the home screen.
 *
 * - `view: 'summary'` (default) — spend-vs-budget card; server-attached
 *   historical `days` make the graph pressable into a TallSheet drill-down.
 * - `view: 'list'` — embedded Spend list (period bars + height-capped day list);
 *   history is attached server-side by show-free-to-spend.
 * - `period` is optional: when omitted, uses home `SPEND_WIDGET_PERIOD`.
 */
export const FreeToSpendWidget = ({
    title,
    period: explicitPeriod,
    accountName,
    days,
    syncteraAccountId,
    view = 'summary',
    selectedPeriodKey,
    periodRollups,
}: FreeToSpendWidgetProps) => {
    const storage = useStorage();
    const period = resolveSpendPeriod(
        explicitPeriod,
        storage.getString(StorageKeys.SPEND_WIDGET_PERIOD),
    );
    const rollup = periodRollups[period];

    const [statusSheetOpen, setStatusSheetOpen] = useState(false);
    const [transactionsSheetOpen, setTransactionsSheetOpen] = useState(false);
    const [selectedTransaction, setSelectedTransaction] =
        useState<Transaction | null>(null);

    const hasTransactions = days != null && days.length > 0;

    const spendData: FreeToSpendDataPoint[] = useMemo(
        () =>
            rollup.spendSeries.map((point) => ({
                date: new Date(point.date),
                amount: point.cumulativeSpendCents,
                transactions: [],
            })),
        [rollup.spendSeries],
    );

    const allSpendData: FreeToSpendDataItem[] = useMemo(
        () =>
            (days ?? []).map((day) => ({
                date: day.date,
                spend: day.spendCents,
                transactions: day.transactions.map(toTransaction),
            })),
        [days],
    );

    const detailsSyncteraAccountId = syncteraAccountId ?? '';
    const enrichedTransaction = useEnrichedTransaction({
        uuid: selectedTransaction?.uuid ?? '',
        syncteraAccountId: detailsSyncteraAccountId,
        enabled:
            view === 'list' &&
            selectedTransaction != null &&
            detailsSyncteraAccountId !== '',
    });

    const {
        currentSpendCents,
        budgetCents,
        status,
        timeLeft,
        remainingLabel,
        remainingValue,
    } = rollup;

    // Mirror the home screen: only over / slowDown open the status explainer.
    const canOpenStatusSheet = status === 'over' || status === 'slowDown';

    if (view === 'list') {
        return (
            <>
                <YStack width="100%" gap={0}>
                    {accountName != null && accountName !== '' ? (
                        <LabelRegular
                            color="$gray1000"
                            fontSize={12}
                            marginBottom={8}
                        >
                            {accountName}
                        </LabelRegular>
                    ) : null}
                    {!hasTransactions ? (
                        <BodyMediumRegular
                            color="$gray1000"
                            paddingVertical={24}
                            textAlign="center"
                        >
                            No spend history available
                        </BodyMediumRegular>
                    ) : (
                        <FreeToSpendTransactionsList
                            spendPeriod={period}
                            allSpendData={allSpendData}
                            budget={budgetCents}
                            isLoadingTransactions={false}
                            initialSelectedPeriodKey={selectedPeriodKey}
                            dayListMaxHeight={
                                FREE_TO_SPEND_EMBEDDED_DAY_LIST_MAX_HEIGHT
                            }
                            onOpenTransaction={(transaction) => {
                                track(AnalyticsUserEvent.FTS_TAP_TRANSACTION, {
                                    view: 'list',
                                    period,
                                });
                                setSelectedTransaction(transaction);
                            }}
                        />
                    )}
                </YStack>
                <SproutTransactionDetailsSheet
                    isOpen={selectedTransaction != null}
                    onClose={() => {
                        setSelectedTransaction(null);
                    }}
                    syncteraAccountId={detailsSyncteraAccountId}
                    account={enrichedTransaction.account}
                    transaction={enrichedTransaction.transaction}
                    isLoading={enrichedTransaction.isLoading}
                    notFound={enrichedTransaction.notFound}
                />
            </>
        );
    }

    return (
        <>
            <SpendingSummaryView
                title={title ?? `${capitalize(period)} Spend`}
                headerSlot={
                    accountName != null && accountName !== '' ? (
                        <LabelRegular color="$gray1000" fontSize={12}>
                            {accountName}
                        </LabelRegular>
                    ) : undefined
                }
                status={status}
                onStatusPress={
                    canOpenStatusSheet
                        ? () => {
                              track(
                                  AnalyticsUserEvent.AI_CHAT_WIDGET_FREE_TO_SPEND_OPEN_STATUS,
                                  { status, period },
                              );
                              setStatusSheetOpen(true);
                          }
                        : undefined
                }
                currentSpendLabel={`Current spend this ${
                    period === 'monthly' ? 'month' : 'week'
                }`}
                currentSpendCents={currentSpendCents}
                budgetCents={budgetCents}
                spendData={spendData}
                period={period}
                isOverBudget={status === 'over'}
                leftInBudgetCents={budgetCents - currentSpendCents}
                timeLeft={timeLeft}
                remainingLabel={remainingLabel}
                remainingValue={remainingValue}
                onGraphPress={
                    hasTransactions
                        ? () => {
                              track(
                                  AnalyticsUserEvent.AI_CHAT_WIDGET_FREE_TO_SPEND_OPEN_TRANSACTIONS,
                                  { status, period },
                              );
                              setTransactionsSheetOpen(true);
                          }
                        : undefined
                }
            />
            <SpendingStatusSheet
                isOpen={statusSheetOpen}
                onClose={() => {
                    setStatusSheetOpen(false);
                }}
                period={period}
            />
            {hasTransactions && (
                <TransactionsSheet
                    isOpen={transactionsSheetOpen}
                    onClose={() => {
                        setTransactionsSheetOpen(false);
                    }}
                    spendPeriod={period}
                    allSpendData={allSpendData}
                    budget={budgetCents}
                    isLoadingTransactions={false}
                    enrichTransactionDetails
                    syncteraAccountIdOverride={syncteraAccountId}
                    initialSelectedPeriodKey={selectedPeriodKey}
                />
            )}
        </>
    );
};
